import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import localeDe from '@angular/common/locales/de';
import { ErrorHandler, LOCALE_ID, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';

import {
  NvConstructionPageModule,
  NvContainerModule,
  NvLoadingSpinnerModule,
  NvPricingFooterModule,
  NvProgressNavigationModule,
} from '@nbg-digital/ui-dpl-components';

import { AppComponent } from './app.component';
import {
  APPLICATION_PATH,
  AppRoutingModule,
  CONCLUSION_PATH,
  PRICING_PATH,
  REGISTRATION_PATH,
  REJECTION_PATH,
  RISK_ASSESSMENT_PATH,
  SUMMARY_PATH,
} from './app-routing.module';

import { environment } from '../environments/environment';
import {
  AppConfigModule,
  AvbUrlDkr,
  CachingInterceptor,
  CmsUrl,
  CredentialsInterceptor,
  HttpLoaderFactory,
  LoadingChunkFailedErrorHandler,
  ProductService,
  ProductServiceFactory,
  ReentryUrlService,
  ReentryUrlServiceFactory,
  siegelUrl,
} from '@nbg-digital/shared/util';
import { ErrorInterceptor, InsuranceApplicationWizardModule, RejectionInterceptor } from '@nbg-digital/wizard/shell';
import { SharedUtilTrackingModule, TrackingService } from '@nbg-digital/shared/util-tracking';
import { DkrTrackingService } from './tracking/dkr-tracking.service';
import { DKR_TRACKING_CONFIG } from './tracking/dkr-tracking-config';
import { StorageAgreementModule } from '@nbg-digital/session-management/feature';
import { APP_CONFIG } from './app-config';

registerLocaleData(localeDe);

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    RouterModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
      defaultLanguage: 'de',
      useDefaultLang: true,
    }),
    AppRoutingModule,
    InsuranceApplicationWizardModule,
    NvProgressNavigationModule,
    NvLoadingSpinnerModule,
    NvPricingFooterModule,
    StorageAgreementModule,
    NvContainerModule,
    NvConstructionPageModule,
    SharedUtilTrackingModule.forRoot({
      service: {
        provide: TrackingService,
        useClass: DkrTrackingService,
      },
      trackingConfig: DKR_TRACKING_CONFIG,
      tealiumConfig: {
        account: environment.tealiumAccount,
        profile: environment.tealiumProfile,
        environment: environment.tealiumEnvironment,
      },
      actionTracking: true,
    }),
    AppConfigModule.forRoot(APP_CONFIG),
  ],
  providers: [
    { provide: HTTP_INTERCEPTORS, useClass: RejectionInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    {
      provide: 'apiUrl',
      useValue: environment.apiUrl,
    },
    {
      provide: 'applicationPath',
      useValue: APPLICATION_PATH,
    },
    {
      provide: 'conclusionPath',
      useValue: CONCLUSION_PATH,
    },
    {
      provide: 'summaryPath',
      useValue: SUMMARY_PATH,
    },
    {
      provide: 'registrationPath',
      useValue: REGISTRATION_PATH,
    },
    {
      provide: 'eweApiUrl',
      useValue: environment.eweApiUrl,
    },
    {
      provide: 'kupoUrl',
      useValue: environment.kupoUrl,
    },
    {
      provide: 'imprintUrl',
      useValue: 'https://www.nuernberger.de/impressum/',
    },
    {
      provide: 'landingPageUrl',
      useValue: environment.landingPageUrl,
    },
    {
      provide: LOCALE_ID,
      useValue: 'de',
    },
    {
      provide: 'pricingPath',
      useValue: PRICING_PATH,
    },
    {
      provide: 'privacyStatementUrl',
      useValue: environment.privacyStatementUrl,
    },
    {
      provide: 'skipMagnumGo',
      useValue: environment.skipMagnumGo,
    },
    {
      provide: 'e2e',
      useValue: environment.e2e,
    },
    {
      provide: 'enableTealiumLogging',
      useValue: environment.enableTealiumLogging,
    },
    {
      provide: 'magnumGoUrl',
      useValue: environment.magnumGoUrl,
    },
    {
      provide: 'magnumGoTenant',
      useValue: environment.magnumGoTenant,
    },
    {
      provide: 'magnumGoLanguage',
      useValue: environment.magnumGoLanguage,
    },
    {
      provide: 'baseUrl',
      useValue: environment.baseUrl,
    },
    {
      provide: ProductService,
      useFactory: ProductServiceFactory,
      deps: ['product'],
    },
    {
      provide: 'product',
      useValue: 'dkr',
    },
    {
      provide: ReentryUrlService,
      useFactory: ReentryUrlServiceFactory,
      deps: ['baseUrl'],
    },
    {
      provide: 'channel',
      useValue: 'online',
    },
    {
      provide: 'riskAssessmentPath',
      useValue: `${RISK_ASSESSMENT_PATH}`,
    },
    {
      provide: 'riskAssessmentCancelUrl',
      useValue: `${environment.baseUrl}/${RISK_ASSESSMENT_PATH}/vorvertragliche-anzeigepflicht`,
    },
    {
      provide: 'riskAssessmentResultUrl',
      useValue: `${environment.baseUrl}/${RISK_ASSESSMENT_PATH}/ergebnis`,
    },
    {
      provide: 'riskAssessmentErrorUrl',
      useValue: `${environment.baseUrl}/${RISK_ASSESSMENT_PATH}/fehler`,
    },
    {
      provide: 'errorPagePath',
      useValue: 'error',
    },
    {
      provide: 'rejectionUrl',
      useValue: `/${REJECTION_PATH}`,
    },
    {
      provide: 'userApiUrl',
      useValue: environment.userApiUrl,
    },
    {
      provide: 'navigationPricingPath',
      useValue: `bedarf/person`,
    },
    {
      provide: 'navigationPricingResultPath',
      useValue: `bedarf/vertrag`,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CredentialsInterceptor,
      multi: true,
    },
    {
      provide: ErrorHandler,
      useClass: LoadingChunkFailedErrorHandler,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CachingInterceptor,
      multi: true,
    },
    {
      provide: 'pathAdviceConnectId',
      useValue: environment.pathAdviceConnectId,
    },
    {
      provide: 'enableChat',
      useValue: environment.enableChat,
    },
    {
      provide: 'enableUserLogin',
      useValue: environment.enableUserLogin,
    },
    {
      provide: 'mainBroker',
      useValue: environment.mainBroker,
    },
    {
      provide: 'enableConstructionPage',
      useValue: environment.enableConstructionPage,
    },
    {
      provide: 'defaultAcquisitionAgent',
      useValue: '658033333',
    },
    {
      provide: 'logo1Url',
      useValue: `${siegelUrl}/8/6/4/c/864c288d4ebac45abb9b839a0828a263f6c11a42/etrustedshops-5sterne-155x200-transp2024.png`,
    },
    {
      provide: 'cmsUrl',
      useValue: CmsUrl,
    },
    {
      provide: 'avbUrl',
      useValue: AvbUrlDkr,
    },
    {
      provide: 'enableMocks',
      useValue: environment.enableMocks,
    },
    {
      provide: 'brokerEntryPoint',
      useValue: `${environment.baseUrl}/${PRICING_PATH}`,
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
